import { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Send, Image, X, Loader2, Check, CheckCheck, ChevronLeft, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { API_URL } from '@/config/api';

export default function AdminChat() {
  const { user, token } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingImage, setPendingImage] = useState(null);
  const [mobileView, setMobileView] = useState('list');
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const prevUnreadRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
  }, []);

  // Fetch conversations with notification check
  const fetchConversations = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/admin/conversations`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (r.ok) {
        const data = await r.json();
        const totalUnread = data.reduce((sum, c) => sum + (c.unread_count || 0), 0);
        
        // Notify on new messages
        if (totalUnread > prevUnreadRef.current && prevUnreadRef.current >= 0) {
          const newConvs = data.filter(c => c.unread_count > 0);
          if (newConvs.length > 0) {
            const name = newConvs[0].customer_name;
            toast.info(`New message from ${name}`, { duration: 5000 });
            // Browser notification
            if (Notification.permission === 'granted') {
              new Notification('GC3H - New Message', { body: `${name} sent a message`, icon: '/favicon.ico' });
            }
          }
        }
        prevUnreadRef.current = totalUnread;
        setConversations(data);
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    fetchConversations();
    const interval = setInterval(fetchConversations, 5000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  // Fetch messages for selected conversation
  const fetchMessages = useCallback(async (convId) => {
    try {
      const r = await fetch(`${API_URL}/api/conversations/${convId}/messages`, {
        headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
      });
      if (r.ok) {
        const data = await r.json();
        setMessages(prev => {
          if (prev.length !== data.length || (data.length > 0 && prev[prev.length - 1]?.message_id !== data[data.length - 1]?.message_id)) {
            return data;
          }
          return prev;
        });
      }
    } catch (e) { console.error(e); }
  }, [token]);

  useEffect(() => {
    if (!selectedConv) return;
    fetchMessages(selectedConv.conversation_id);
    // Mark as read
    fetch(`${API_URL}/api/conversations/${selectedConv.conversation_id}/read`, {
      method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include'
    }).catch(() => {});
    const interval = setInterval(() => fetchMessages(selectedConv.conversation_id), 3000);
    return () => clearInterval(interval);
  }, [selectedConv, fetchMessages, token]);

  useEffect(() => { scrollToBottom(); }, [messages, scrollToBottom]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if ((!newMessage.trim() && !pendingImage) || !selectedConv) return;
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/api/conversations/${selectedConv.conversation_id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        credentials: 'include',
        body: JSON.stringify({ content: newMessage.trim(), image_url: pendingImage?.url || null, image_name: pendingImage?.name || null })
      });
      if (!r.ok) throw new Error('Failed');
      const sent = await r.json();
      setMessages(prev => [...prev, sent]);
      setNewMessage('');
      removePendingImage();
      scrollToBottom();
    } catch (e) { toast.error('Failed to send message'); }
    finally { setSending(false); }
  };

  const handleImageSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5MB'); return; }
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API_URL}/api/chat/upload-image`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` }, credentials: 'include', body: fd
      });
      if (!r.ok) throw new Error('Upload failed');
      const data = await r.json();
      setPendingImage({ url: `${API_URL}${data.image_url}`, name: data.filename, preview: URL.createObjectURL(file) });
    } catch (e) { toast.error('Upload failed'); }
    finally { setUploadingImage(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const removePendingImage = () => {
    if (pendingImage?.preview) URL.revokeObjectURL(pendingImage.preview);
    setPendingImage(null);
  };

  const getImageUrl = (url) => url?.startsWith('/api') ? `${API_URL}${url}` : url;

  const totalUnread = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);

  return (
    <div data-testid="admin-chat">
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="font-heading text-xl md:text-2xl font-bold text-secondary-900">Messages</h1>
          <p className="text-sm text-muted-foreground">
            {totalUnread > 0 ? `${totalUnread} unread message${totalUnread > 1 ? 's' : ''}` : 'Respond to customer enquiries'}
          </p>
        </div>
        {totalUnread > 0 && (
          <div className="flex items-center gap-2 bg-primary/10 px-3 py-1.5 rounded-full">
            <Bell className="h-4 w-4 text-primary animate-pulse" />
            <span className="text-sm font-semibold text-primary">{totalUnread}</span>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-13rem)]">
          {/* Conversations List */}
          <div className={`lg:col-span-1 bg-white rounded-xl border border-border overflow-hidden flex flex-col ${mobileView === 'chat' ? 'hidden lg:flex' : 'flex'}`}>
            <div className="px-4 py-3 border-b bg-muted/30">
              <h2 className="font-semibold text-sm text-secondary-900">Conversations</h2>
            </div>
            <div className="flex-1 overflow-y-auto">
              {conversations.length > 0 ? (
                <div className="divide-y divide-border">
                  {conversations.map(conv => (
                    <button
                      key={conv.conversation_id}
                      onClick={() => { setSelectedConv(conv); setMobileView('chat'); }}
                      className={`w-full p-3 text-left hover:bg-muted/50 transition-colors ${
                        selectedConv?.conversation_id === conv.conversation_id ? 'bg-primary/5 border-l-2 border-l-primary' : ''
                      }`}
                      data-testid={`conv-${conv.conversation_id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm flex-shrink-0">
                          {(conv.customer_name || '?')[0].toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-sm text-secondary-900 truncate">{conv.customer_name}</p>
                            {conv.unread_count > 0 && (
                              <Badge className="bg-primary text-white text-[10px] h-5 min-w-[20px] flex items-center justify-center">{conv.unread_count}</Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{conv.last_message || 'No messages yet'}</p>
                          {conv.last_message_at && (
                            <p className="text-[10px] text-muted-foreground/60 mt-0.5">
                              {new Date(conv.last_message_at).toLocaleString('en-ZA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <MessageSquare className="h-10 w-10 mb-3 opacity-30" />
                  <p className="text-sm">No conversations yet</p>
                </div>
              )}
            </div>
          </div>

          {/* Chat Area — fixed height with scroll */}
          <div className={`lg:col-span-2 bg-white rounded-xl border border-border flex flex-col overflow-hidden ${mobileView === 'list' ? 'hidden lg:flex' : 'flex'}`}>
            {selectedConv ? (
              <>
                {/* Chat header — fixed */}
                <div className="px-4 py-3 border-b bg-white flex items-center gap-3 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => { setSelectedConv(null); setMobileView('list'); }} className="lg:hidden p-1">
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                    {(selectedConv.customer_name || '?')[0].toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm text-secondary-900 truncate">{selectedConv.customer_name}</p>
                    <p className="text-xs text-muted-foreground truncate">{selectedConv.customer_email}</p>
                  </div>
                </div>

                {/* Messages — scrollable */}
                <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50" style={{ minHeight: 0 }}>
                  <div className="space-y-3 max-w-2xl mx-auto">
                    {messages.map(msg => {
                      const isStaff = msg.sender_role !== 'customer';
                      const isOwn = msg.sender_id === user?.user_id;
                      const time = new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                      return (
                        <div key={msg.message_id} className={`flex ${isStaff ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] px-3.5 py-2.5 ${
                            isStaff
                              ? 'bg-primary text-white rounded-2xl rounded-br-sm'
                              : 'bg-white border border-border text-secondary-900 rounded-2xl rounded-bl-sm shadow-sm'
                          }`}>
                            {!isStaff && <p className="text-[11px] font-semibold text-primary mb-0.5">{msg.sender_name}</p>}
                            {msg.image_url && (
                              <img src={getImageUrl(msg.image_url)} alt="Attachment"
                                className="max-w-full rounded-xl mb-2 max-h-48 cursor-pointer"
                                onClick={() => window.open(getImageUrl(msg.image_url), '_blank')} />
                            )}
                            {msg.content && <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>}
                            <div className={`flex items-center gap-1 mt-1 ${isStaff ? 'justify-end' : ''}`}>
                              <span className={`text-[10px] ${isStaff ? 'text-white/60' : 'text-muted-foreground'}`}>{time}</span>
                              {isOwn && (msg.read_count > 1 ? <CheckCheck className="h-3 w-3 text-white/60" /> : <Check className="h-3 w-3 text-white/60" />)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                </div>

                {/* Image preview — fixed above reply */}
                {pendingImage && (
                  <div className="px-4 py-2 border-t bg-muted/30 flex items-center gap-2 flex-shrink-0">
                    <img src={pendingImage.preview} alt="Preview" className="h-12 w-12 object-cover rounded-lg" />
                    <span className="text-xs truncate flex-1">{pendingImage.name}</span>
                    <Button variant="ghost" size="sm" onClick={removePendingImage} className="p-1"><X className="h-4 w-4" /></Button>
                  </div>
                )}

                {/* Reply box — ALWAYS fixed at bottom */}
                <form onSubmit={sendMessage} className="px-4 py-3 border-t bg-white flex-shrink-0">
                  <div className="flex gap-2 max-w-2xl mx-auto">
                    <input type="file" ref={fileInputRef} onChange={handleImageSelect} accept="image/*" className="hidden" />
                    <Button type="button" variant="outline" size="icon" onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingImage || sending} className="h-10 w-10 flex-shrink-0" data-testid="attach-image-btn">
                      {uploadingImage ? <Loader2 className="h-4 w-4 animate-spin" /> : <Image className="h-4 w-4" />}
                    </Button>
                    <Input value={newMessage} onChange={e => setNewMessage(e.target.value)} placeholder="Type your reply..."
                      disabled={sending} className="flex-1" data-testid="admin-chat-input" />
                    <Button type="submit" disabled={(!newMessage.trim() && !pendingImage) || sending}
                      className="bg-primary h-10 px-4 flex-shrink-0" data-testid="admin-send-btn">
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </form>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-muted-foreground">
                  <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p className="font-medium">Select a conversation</p>
                  <p className="text-sm mt-1">Choose from the list to view messages</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
